import { CLASS_ROOT, CLASS_SLIDE } from "@/components/Carousel/constants";

export default class SameHeight {
  constructor(element) {
    this.element = element;
    this.children = null;
    this.updateHeight = this.updateHeight.bind(this);
    this.element.ODS_SameHeight = this;
    this.intervalId = null;
    this.init();
  }

  temporarilyShowHiddenElements() {
    this.originalDisplayStates = [];

    this.children.forEach((child) => {
      const tabPanel = child.closest('[role="tabpanel"]');
      const modal = child.closest('[role="dialog"]');

      if (tabPanel && tabPanel.hasAttribute("hidden")) {
        this.originalDisplayStates.push({
          element: tabPanel,
          display: tabPanel.style.display,
        });
        tabPanel.style.display = "block";
        tabPanel.removeAttribute("hidden");
      }

      if (modal && modal.hasAttribute("aria-hidden")) {
        this.originalDisplayStates.push({
          element: modal,
          display: modal.style.display,
        });
        modal.style.display = "block";
        modal.removeAttribute("aria-hidden");
      }
    });
  }

  restoreHiddenElements() {
    this.originalDisplayStates.forEach(({ element, display }) => {
      element.style.display = display;

      if (element.getAttribute("role") === "tabpanel") {
        element.setAttribute("hidden", true);
      } else if (element.getAttribute("role") === "dialog") {
        element.setAttribute("aria-hidden", true);
      }
    });
  }

  getCarouselElement() {
    const selector = `[data-carousel], .${CLASS_ROOT}`;
    if (this.element.matches(selector)) {
      return this.element;
    }
    return this.element.closest(selector);
  }

  isInsideOrIsCarousel() {
    return this.getCarouselElement();
  }

  getCarouselSlides() {
    const carousel = this.getCarouselElement();
    return carousel ? carousel.querySelectorAll(`.${CLASS_SLIDE}`) : [];
  }

  updateHeight() {
    this.temporarilyShowHiddenElements();

    const allSameHeightChildren = this.element.querySelectorAll(
      '[data-same-height-child="true"]',
    );

    allSameHeightChildren.forEach((child) => {
      if (child.style.height) {
        child.style.height = "";
      }
    });

    const groupedElements = this.groupElementsByTypeAndPosition(
      allSameHeightChildren,
    );
    const carouselElement = this.isInsideOrIsCarousel();

    for (const [_groupName, elements] of Object.entries(groupedElements)) {
      if (!elements.length) continue;

      const rows = this.groupElementsIntoRows(elements, carouselElement);

      rows.forEach((row) => {
        if (row.length > 1) {
          const maxHeight = Math.max(...row.map((child) => child.offsetHeight));
          row.forEach((child) => {
            child.style.height = `${maxHeight}px`;
          });
        } else {
          row.forEach((child) => {
            if (child.style.height) {
              child.style.height = null;
            }
          });
        }
      });
    }

    this.restoreHiddenElements();
  }

  groupElementsIntoRows(elements, carouselElement) {
    if (carouselElement) {
      return [elements];
    }

    const rows = [];
    let currentRow = [];
    let lastOffset = elements[0].getBoundingClientRect().top;

    for (const child of elements) {
      const childOffset = child.getBoundingClientRect().top;

      if (childOffset === lastOffset) {
        currentRow.push(child);
      } else {
        rows.push(currentRow);
        currentRow = [child];
        lastOffset = childOffset;
      }
    }

    rows.push(currentRow);
    return rows;
  }

  groupElementsByTypeAndPosition(elements) {
    const groups = {};

    elements.forEach((element) => {
      const groupKey =
        element.getAttribute("data-same-height-group") || "default";

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }

      groups[groupKey].push(element);
    });

    return groups;
  }

  init() {
    this.children = this.element.querySelectorAll(
      '[data-same-height-child="true"]',
    );

    const imgLoaded = [];
    this.children.forEach((child) => {
      const images = child.querySelectorAll("img");
      images.forEach((img) => {
        if (!img.complete) {
          imgLoaded.push(img);
        }
      });
    });

    const carouselElement = this.isInsideOrIsCarousel();

    const performInitialUpdate = () => {
      if (carouselElement) {
        this.waitForCarouselThenUpdate(carouselElement);
      } else {
        this.updateHeight();
      }
    };

    if (imgLoaded.length === 0) {
      performInitialUpdate();
    } else {
      this.waitForImagesToLoad(imgLoaded, performInitialUpdate);
    }

    window.addEventListener("resize", this.updateHeight);

    if (carouselElement) {
      this.setupCarouselListeners(carouselElement);
    }
  }

  waitForCarouselThenUpdate(carouselElement) {
    if (carouselElement.ODS_Carousel?.instance) {
      requestAnimationFrame(() => {
        this.updateHeight();
      });
    } else {
      setTimeout(() => this.waitForCarouselThenUpdate(carouselElement), 50);
    }
  }

  waitForImagesToLoad(imgLoaded, callback) {
    let attempts = 0;
    this.intervalId = setInterval(() => {
      let completed = false;

      imgLoaded.forEach((img, index, array) => {
        if (img.complete) {
          array.splice(index, 1);
          completed = true;
        }
      });

      if (completed && this.updateHeight) {
        callback();
      }

      if (++attempts >= 15 || imgLoaded.length === 0) {
        if (imgLoaded.length && this.updateHeight) {
          callback();
        }
        window.clearInterval(this.intervalId);
      }
    }, 400);
  }

  setupCarouselListeners(carouselElement) {
    if (carouselElement.ODS_Carousel?.instance) {
      const { instance } = carouselElement.ODS_Carousel;

      ["resize", "update", "slideChange"].forEach((event) => {
        instance.on(event, () => {
          requestAnimationFrame(() => {
            this.updateHeight();
          });
        });
      });
    } else {
      setTimeout(() => this.setupCarouselListeners(carouselElement), 50);
    }
  }

  update() {
    this.destroy();
    this.init();
  }

  destroy() {
    window.removeEventListener("resize", this.updateHeight);
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  static getInstance(el) {
    return el?.ODS_SameHeight || null;
  }
}
